/**
 * Task #2488 — Dimensione "piano" della Dashboard AI / RAG Analytics.
 *
 * Il traffico tracciato in `rag_pipeline_logs` confluisce da tre piani
 * funzionali distinti, derivabili dal tipo di agente (JOIN su
 * `ai_voice_agents` / `ai_chatbot_agents` / `ai_personal_assistants`,
 * nessuna colonna dedicata in schema):
 *
 *   - `rag`     → chatbot con knowledge base (Q&A documentale, synthesis)
 *   - `agentic` → Personal Assistant con tool-calling (+ `ai_agent` generico)
 *   - `voice`   → agenti vocali realtime
 *
 * Le metriche di QUALITÀ RAG (confidence, inferential, cache hit, query
 * problematiche/zero-risultati, KB coverage) e il coherence check
 * embedder↔chat-model hanno senso SOLO sul piano `rag`. Questo modulo è la
 * singola fonte di verità della mappa `agent_type → plane`, riusata sia dal
 * backend (query stats + coherence) sia dal frontend (badge/segmentazione).
 */

export type RagPlane = 'rag' | 'agentic' | 'voice';

export type RagAgentType = 'voice' | 'chatbot' | 'personal_assistant' | 'profiling' | 'ai_agent';

export const RAG_PLANES: readonly RagPlane[] = ['rag', 'agentic', 'voice'] as const;

export const RAG_PLANE_LABELS: Record<RagPlane, string> = {
  rag: 'RAG',
  agentic: 'Agentico',
  voice: 'Voce',
};

export const RAG_PLANE_DESCRIPTIONS: Record<RagPlane, string> = {
  rag: 'Chatbot con knowledge base — Q&A documentale',
  agentic: 'Personal Assistant con tool-calling',
  voice: 'Agenti vocali realtime',
};

/**
 * Mappa il tipo di agente al piano funzionale. Qualsiasi tipo non
 * riconosciuto (es. `ai_agent` generico) ricade su `agentic`.
 */
export function agentTypeToPlane(agentType: string): RagPlane {
  switch (agentType) {
    case 'voice':
      return 'voice';
    case 'chatbot':
      return 'rag';
    case 'personal_assistant':
      return 'agentic';
    case 'profiling':
      return 'agentic';
    default:
      return 'agentic';
  }
}

/** Conteggio query segmentato per piano. */
export interface RagPlaneCounts {
  rag: number;
  agentic: number;
  voice: number;
}
